import { action, makeObservable, observable } from 'mobx';
import { StateType } from '@wix/ambassador-iam-authentication-v1-authentication/types';

/** The states an IAM login can wait at on the login form or the code screen. */
export type WaitingState =
  | StateType.REQUIRE_FIRST_FACTOR
  | StateType.REQUIRE_LOGIN_CHALLENGE;

/**
 * The IAM login in progress: the state token of its last reply, the state it
 * waits at, and the email it was issued for. The next login or Verify call for
 * that email sends the token, so the server continues the same login.
 */
export class LoginFlow {
  token?: string = undefined;
  state?: WaitingState = undefined;
  email?: string = undefined;

  constructor() {
    makeObservable(this, {
      token: observable,
      state: observable,
      email: observable,
      record: action.bound,
      clear: action.bound,
    });
  }

  /** The login now waits at `state` with `token`. */
  record(
    email: string | undefined,
    token: string | undefined,
    state: WaitingState,
  ) {
    if (!token) {
      this.clear();
      return;
    }
    this.email = email;
    this.token = token;
    this.state = state;
  }

  /** The login finished, moved on to MFA, or can't be continued. */
  clear() {
    this.email = undefined;
    this.token = undefined;
    this.state = undefined;
  }

  /** The token to send with the next call for this email, in any state. */
  tokenFor(email: string) {
    return this.email === email ? this.token : undefined;
  }

  /** The token, when the login for this email waits at `state`. */
  tokenAt(email: string, state: WaitingState) {
    return this.state === state ? this.tokenFor(email) : undefined;
  }
}
